"use client";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { OptionsContext } from "@/provider/options-provider";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import React, { useContext, useState } from "react";

export function CombinePdfButton(props: {
  className?: React.ComponentProps<"button">["className"];
}) {
  const { groupSummaryItems, searchOptions } = useContext(OptionsContext);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const disabled = groupSummaryItems.length === 0 || loading;

  const handleClick = async () => {
    setLoading(true);
    try {
      const contentIdentifiers = groupSummaryItems.map(
        (item) => `${item.type}:${item.id}`,
      );
      const publishTicker = "PERCY Index";
      const ticker =
        searchOptions.ticker && searchOptions.ticker.trim()
          ? searchOptions.ticker
          : "";

      const requestBody = {
        ContentIdentifiers: contentIdentifiers,
        Title: ticker ? `${ticker} Summary` : "Summary",
        PublishTicker: publishTicker,
        Email: true,
      };

      const response = await fetch("/api/pdf/compose", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        const url = result?.url;
        const title = requestBody.Title;

        const isMobile =
          /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
          (/Macintosh/i.test(navigator.userAgent) &&
            navigator.maxTouchPoints > 1);
        const canShare =
          isMobile && typeof navigator.share === "function" && !!url;

        toast({
          title: "PDF ready",
          description: url
            ? React.createElement(
                "div",
                { className: "flex flex-col gap-2" },
                React.createElement(
                  "a",
                  {
                    href: url,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    className: "underline font-medium",
                  },
                  "Open combined PDF",
                ),
                canShare
                  ? React.createElement(
                      "button",
                      {
                        type: "button",
                        className:
                          "inline-flex items-center gap-1 underline font-medium text-left",
                        onClick: () => {
                          navigator
                            .share({ title, url })
                            .catch(() => {});
                        },
                      },
                      "Share / Save PDF",
                    )
                  : null,
              )
            : "Combined PDF request submitted.",
        });
      } else {
        toast({
          title: "Error",
          description: `PDF generation failed. Please report to Shareholder Support. (${JSON.stringify(await response.json())})`,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      className={props.className}
      type="button"
      tabIndex={-1}
      variant={"outline"}
      size={"icon"}
      disabled={disabled}
      title="Request combined PDF"
    >
      {loading ? (
        <Spinner size={16} />
      ) : (
        <PictureAsPdfIcon className="h-4 w-4" />
      )}
    </Button>
  );
}
